import ListFilters from "@/common/components/ListFilters";
import ListHeader from "@/common/components/ListHeader";
import Table from "@/common/components/Table";
import TextField from "@/common/components/TextField";
import { Mapper } from "@/common/services/mapper";
import { useFeeTemplates } from "@/features/feeTemplates/hooks/useFeeTemplates";
import { FeeTemplatesTransformer } from "@/features/feeTemplates/services/feeTemplatesTransformer";
import { FeeTemplateData, FeeTemplateTableItem } from "@/features/feeTemplates/types/feeTemplates.type";
import { PiggyBank, Search } from "lucide-react";
import { options as applicableEntitiesOptions } from "../../constants/applicableEntity.constant";
import { defaultColumns } from "../../constants/columns.constant";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import useDebounce from "@/common/hooks/useDebounce";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import BulkActionNewEntryModal from "@/common/components/BulkActionNewEntryModal";
import { Link } from "react-router-dom";
import Button from "@/common/components/Button";
import { useQueryClient } from "@tanstack/react-query";

const FeeTemplates = () => {
  const { filterParams, setFilterParams } = useFilterParams();

  const queryClient = useQueryClient();

  const [search, setSearch] = useDebounce(filterParams.search || '');

  const {
    data: feeTemplateOptions,
    meta,
    isPending: isPendingFeeTemplates,
    isLoading: isLoadingFeeTemplates,
    error: feeTemplateError = {} as any,
    refetch: refetchFeeTemplate,
  } = useFeeTemplates({
    filters: {
      search: search,
      page: filterParams.page || 1,
      category: filterParams.category?.value,
    },
    mapper: Mapper.mapWithTransformer<FeeTemplateData[], FeeTemplateTableItem[]>({
      transformer: FeeTemplatesTransformer.tableTransformer('Admin::Billing::FeeTemplates::Show')
    })
  });

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        Icon={<PiggyBank className="w-10 h-10" strokeWidth={1.5} />}
        title="Tarifa"
        actions={
          <div className="flex gap-4">
            <BulkActionNewEntryModal
              operationKlassToCheckPermission="Admin::BulkActions::Entries::RequestTemplate"
              options={[
                {
                  label: 'Editar tarifas contratuais do cliente',
                  actionerKlass: 'BulkActions::Admin::ClientContractFeesEditor',
                },
                {
                  label: 'Editar tarifas contratuais do estabelecimento',
                  actionerKlass:
                    'BulkActions::Admin::EstablishmentContractFeesEditor',
                },
              ]}
              onEntryCompleted={() => {
                queryClient.invalidateQueries({
                  queryKey: ['feeTemplates'],
                });
              }}
            />
            <Link
              to="create"
              className={
                isPendingFeeTemplates || feeTemplateError?.status === 403
                  ? 'cursor-default'
                  : ''
              }
            >
              <Button
                operationKlassToCheckPermission="Admin::Billing::FeeTemplates::Create"
                className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
                disabled={
                  isPendingFeeTemplates || feeTemplateError?.status === 403
                }
                data-testid="access_fee_template_create_button"
              >
                Nova tarifa
              </Button>
            </Link>
          </div>
        }
      />

      <ErrorMessageWrapper
        className="h-[calc(100vh-128px)]"
        statusCode={feeTemplateError?.status}
        refetchFunction={refetchFeeTemplate}
      >
        <div className="h-[calc(100vh-128px)] flex flex-col gap-6">
          <div className="flex flex-col gap-4">
            <TextField
              LeftIcon={<Search />}
              placeholder="Pesquisar"
              className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
              onChange={(e) => {
                setSearch((e.target as HTMLInputElement).value);
                setFilterParams((prev) => ({ ...prev, search: (e.target as HTMLInputElement).value }))
              }}
              value={filterParams.search}
            />
            <ListFilters
              filters={[
                {
                  className: 'min-w-[160px]',
                  onChange: (e) => setFilterParams((prev) => ({ ...prev, category: e })),
                  value: filterParams.category,
                  options: [{ label: 'Todas', value: '' }, ...(applicableEntitiesOptions || [])],
                  placeholder: 'Tipo'
                }
              ]}
            />
          </div>

          <Table
            columns={defaultColumns}
            isLoadingData={isLoadingFeeTemplates}
            page={filterParams.page || 1}
            rows={feeTemplateOptions}
            totalPages={meta.total_pages}
            totalCount={meta.total}
            hasPagination
            setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
          />
        </div>
      </ErrorMessageWrapper>
    </div>
  );
};

export default FeeTemplates;
